import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";

export type PageDensity = "compact" | "comfortable";

/**
 * Who owns the page's horizontal edge padding.
 * - `"self"` — Page pads its own left/right gutter (it spans the full width, as
 *   in Hubble where Page is the top-level route content).
 * - `"host"` — the app's route container already insets the content (e.g. the
 *   dashboard's responsive site margin), so Page must NOT add a gutter or the
 *   content would double-inset and fall out of alignment with the nav.
 */
export type PageGutter = "self" | "host";

export interface PageChrome {
  /** Suffix appended to document.title, e.g. "Credit Cards — FreightSimple". */
  appName: string;
  /** Default density for pages in this app (a Page may override per-instance). */
  density: PageDensity;
  gutter: PageGutter;
}

// Defaults are Hubble's existing behaviour, so a Page with no provider around it
// renders exactly as before (compact, self-guttered, "— Hubble" title).
const DEFAULT_CHROME: PageChrome = {
  appName: "Hubble",
  density: "compact",
  gutter: "self",
};

const PageChromeContext = createContext<PageChrome>(DEFAULT_CHROME);

export function usePageChrome(): PageChrome {
  return useContext(PageChromeContext);
}

/**
 * Sets the page chrome for an app — mount once at the app root. Dashboard uses
 * `appName="FreightSimple" density="comfortable" gutter="host"`; Hubble can rely
 * on the defaults.
 */
export function PageChromeProvider({
  appName = DEFAULT_CHROME.appName,
  density = DEFAULT_CHROME.density,
  gutter = DEFAULT_CHROME.gutter,
  children,
}: {
  appName?: string;
  density?: PageDensity;
  gutter?: PageGutter;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ appName, density, gutter }),
    [appName, density, gutter],
  );
  return (
    <PageChromeContext.Provider value={value}>
      {children}
    </PageChromeContext.Provider>
  );
}
